import React, { useEffect } from "react";
import { motion, useMotionValue, useSpring } from "motion/react";
import style from "./style.module.scss";

type Tier = 1 | 2 | 3;
interface Props {
  size?: number;
  strokeWidth?: number;
  points?: number;
  centerImage?: string;
  tier: number | undefined;
}

const TierRing: React.FC<Props> = ({
  size = 250,
  strokeWidth = 30,
  tier = 3,
  centerImage,
}) => {
  // progress ring defend on tier: 1/3, 2/3, 3/3
  const tierProgress = { 3: 1 / 3, 2: 2 / 3, 1: 1 };
  const progressRatio = tierProgress[tier as Tier];

  // calculate
  const radius = size / 2;
  const circumference = 2 * Math.PI * radius;

  // Motion value
  const dashOffset = useMotionValue(circumference);
  const animatedOffset = useSpring(dashOffset, { stiffness: 120, damping: 20 });

  useEffect(() => {
    dashOffset.set(circumference * (1 - progressRatio));
  }, [progressRatio, circumference, dashOffset]);

  // Tick
  const tickAngles = [0, 120, 240];
  const tickLength = strokeWidth * 0.7;

  const padding = strokeWidth;
  const totalSize = size + padding * 2;
  const cx = totalSize / 2;
  const cy = totalSize / 2;

  return (
    <div
      className={style.wrapper}
      style={{ width: totalSize, height: totalSize, position: "relative" }}
    >
      <svg
        width={totalSize}
        height={totalSize}
        viewBox={`0 0 ${totalSize} ${totalSize}`}
        style={{ overflow: "visible" }}
      >
        <defs>
          <linearGradient id="tierGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8c6af5" />
            <stop offset="100%" stopColor="#35198c" />
          </linearGradient>
        </defs>

        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#111"
          strokeWidth={strokeWidth}
          style={{
            filter: "drop-shadow(0 0 6px #444) drop-shadow(0 0 10px #222)",
          }}
        />

        {/* Vòng progress */}
        <motion.circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="url(#tierGradient)"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={animatedOffset}
          style={{
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
            filter:
              "drop-shadow(0 0 8px #8c6af5) drop-shadow(0 0 12px #35198c)",
          }}
        />

        {tickAngles.map((angle, i) => (
          <g key={i} transform={`rotate(${angle} ${cx} ${cy})`}>
            <line
              x1={cx}
              y1={cy - radius - tickLength}
              x2={cx}
              y2={cy - radius + tickLength}
              className={style.tick}
            />
          </g>
        ))}
      </svg>

      {centerImage && (
        <img
          src={centerImage}
          alt="center"
          className={style.avatar}
          style={{
            width: size * 0.38,
            height: size * 0.38,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
    </div>
  );
};

export default TierRing;
